import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  CouponPackMaster,
  CreateCouponPackMasterInput,
  useCreateCouponPackMasterMutation,
} from '../../../../graphql/generated';
import { useHistory } from 'react-router-dom';
import { useFormCouponPackMaster } from './form-coupon-pack';
import { CreateCouponPackMasterPageProps } from '../Create';
import { CouponPackMasterFormInput } from '../../../../types/couponPackMaster';
import { couponImageProps, logoImageProps } from './utils';
import { useUploadImage } from './upload-image';
import Bugsnag from '@bugsnag/js';
import { DateTime } from 'luxon';
function createInitialValue(): CouponPackMasterFormInput {
  return {
    unAvailable: false,
    couponName: '',
    displayCouponName: '',
    couponCode: '',
    displayStartDate: null,
    displayEndDate: null,
    validityStartDate: null,
    validityStartTime: null,
    validityEndDate: null,
    validityEndTime: DateTime.local().set({ hour: 23, minute: 59 }),
    description: '',
    note: '',
    products: [
      {
        id: '',
        couponMasterName: '',
        quantity: '',
        maxCouponsPerOrder: '',
        couponMasterCode: '',
        imageUrl: '',
        validityStartDateTime: '',
        validityEndDateTime: '',
        displayEndDate: '',
        displayStartDate: '',
        displayValidityDate: false,
        couponNameHtml: '',
      },
    ],
    status: '',
    imageUrl: '',
  };
}

export const useCreateCouponPackMaster = (couponPackMasterId?: string): CreateCouponPackMasterPageProps => {
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [submitStepMessage, setSubmitStepMessage] = useState('読み込み中...');

  const useUpload = useUploadImage();
  const {
    loading: imageLoading,
    uploadImages,
    validateImageFiles,
    validateAndSetLogoImage,
    setDeleteLogoImage,
    validateAndSetCouponImage,
    setDeleteCouponImage,
    selectedFiles,
    shouldUploadImage,
  } = useUpload;

  const [createCouponPackMasterMutation] = useCreateCouponPackMasterMutation();

  const { validateDateAndGetDateTime, loading, setLoading, organization } = useFormCouponPackMaster();

  const [previousValues, setPreviousValues] = useState<CouponPackMasterFormInput>();

  useEffect(() => {
    if (couponPackMasterId) {
      setLoading(true);
    } else {
      setPreviousValues(createInitialValue);
      setLoading(false);
    }
  }, [couponPackMasterId, enqueueSnackbar]);

  const onSubmit = useCallback(
    async (input: CouponPackMasterFormInput) => {
      closeSnackbar();

      if (!organization) {
        setLoading(false);
        enqueueSnackbar('データ読み込み中です。もう少々おまちください。', {
          variant: 'warning',
          autoHideDuration: 10000,
        });
        return;
      }

      if (validateImageFiles()) return;

      const validatedDateTime = validateDateAndGetDateTime(input);
      if (validatedDateTime.invalid) return;

      const { displayStartDateTime, displayEndDateTime, validityStartDateTime, validityEndDateTime } =
        validatedDateTime;

      setLoading(true);
      setSubmitStepMessage('マスタデータを登録しています');

      const requestParameters: CreateCouponPackMasterInput = {
        available: !input.unAvailable,
        couponCode: input.couponCode,
        couponName: input.couponName,
        displayCouponName: input.displayCouponName,

        organizationId: organization?.id,

        displayStartDate: displayStartDateTime.toISO(),
        displayEndDate: displayEndDateTime.toISO(),

        validityStartDateTime: validityStartDateTime ? validityStartDateTime.toISO() : '',
        validityEndDateTime: validityEndDateTime.toISO(),

        description: input.description,
        note: input.note,
        products: input.products.map((p) => ({
          quantity: Number(p.quantity),
          couponMasterId: p.id,
          couponMasterName: p.couponMasterName,
          maxCouponsPerOrder: p.maxCouponsPerOrder ? Number(p.maxCouponsPerOrder) : null,
        })),

        logoImage: { key: '' },
        couponImage: { key: '' },
      };

      const { data, errors } = await createCouponPackMasterMutation({
        variables: {
          input: requestParameters,
        },
      });

      if (!data) {
        setLoading(false);
        enqueueSnackbar(`回数券マスタデータの登録に失敗しました: ${errors}`, {
          variant: 'error',
          persist: true,
        });
        return;
      }

      if (!shouldUploadImage(data.createCouponPackMaster.id)) return;

      setSubmitStepMessage('画像を登録しています...');

      try {
        await uploadImages(data.createCouponPackMaster as CouponPackMaster);

        enqueueSnackbar(`回数券マスタデータを登録しました`, {
          variant: 'success',
        });
      } catch (err) {
        Bugsnag.notify(err);
        enqueueSnackbar(
          '回数券マスタデータは登録しましたが、画像登録に失敗しました。画像のみ再度アップロードしてください',
          {
            variant: 'warning',
            autoHideDuration: 10000,
          }
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        history.push({
          pathname: `/coupon-pack-masters/${data.createCouponPackMaster.id}`,
          search: history.location.search,
        });
      } finally {
        setLoading(false);
      }

      history.push({
        pathname: `/coupon-pack-masters/${data.createCouponPackMaster.id}`,
        search: history.location.search,
      });
    },
    [createCouponPackMasterMutation, enqueueSnackbar, selectedFiles, useUpload]
  );
  return {
    onSubmit,
    loading: loading || imageLoading,
    previousValues,
    submitStepMessage,
    logoImageProps: logoImageProps({
      selectedFile: selectedFiles.logoImage,
      validateAndSetFile: validateAndSetLogoImage,
      setDeleteFile: setDeleteLogoImage,
    }),
    couponImageProps: couponImageProps({
      selectedFile: selectedFiles.couponImage,
      validateAndSetFile: validateAndSetCouponImage,
      setDeleteFile: setDeleteCouponImage,
    }),
  };
};
